/**
 * The company's AI key, as a screen may know it.
 *
 * **Status comes from the database; actions go through the server.** The
 * status is `secret_status()`, which answers only somebody holding
 * `integrations.manage` and returns whether a key is saved and how its last
 * check went — no byte of the key, sealed or not. Saving, verifying and
 * removing are routes, because each needs the master key, which a browser
 * never holds.
 *
 * **The key is write-only.** Once saved it is never sent back, so there is no
 * state here that could hold it: the screen keeps what is being typed until
 * the moment it is submitted, and then clears it.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { apiPost } from '../lib/api';

export type VerificationStatus = 'unverified' | 'verified' | 'failed';

export type VerificationCategory =
    | 'invalid_credential' | 'permission_denied' | 'quota_exceeded'
    | 'provider_unavailable' | 'network_error' | 'unexpected_response' | 'unreadable';

export interface AiCredentialStatus {
    configured: boolean;
    provider: string | null;
    configuredAt: string | null;
    updatedAt: string | null;
    verificationStatus: VerificationStatus | null;
    verificationCategory: VerificationCategory | null;
    verifiedAt: string | null;
    checkedAt: string | null;
}

export const NOT_CONFIGURED: AiCredentialStatus = {
    configured: false, provider: null, configuredAt: null, updatedAt: null,
    verificationStatus: null, verificationCategory: null, verifiedAt: null, checkedAt: null,
};

export type Verification = { ok: true } | { ok: false; category: VerificationCategory };

/** What each failure means, in words a person can act on. */
export const VERIFICATION_WORDS: Record<VerificationCategory, string> = {
    invalid_credential: 'The provider did not accept this key. Check you copied all of it, or create a new one.',
    permission_denied: 'The key is valid but not allowed to write with Gemini. Google may have restricted its project (Google AI Studio says why), the Gemini API may be off for it, or the key may be restricted.',
    quota_exceeded: 'The key works but its quota is used up. Check its limits or billing with the provider.',
    provider_unavailable: 'The provider was not answering. The key is saved — try verifying again in a few minutes.',
    network_error: 'We could not reach the provider. The key is saved — try verifying again in a few minutes.',
    unexpected_response: 'The provider gave an answer we did not recognise. The key is saved — try verifying again later.',
    unreadable: 'The saved key could not be read. Replace it with the key from your provider.',
};

/** The providers a company can choose. One, until another is built and tested. */
export const AI_PROVIDERS = [{ id: 'google', label: 'Google Gemini' }] as const;

export const providerLabel = (id: string | null): string =>
    AI_PROVIDERS.find(p => p.id === id)?.label ?? 'AI provider';

interface StatusRow {
    configured: boolean;
    provider: string;
    configured_at: string;
    updated_at: string;
    verification_status: VerificationStatus;
    verification_category: VerificationCategory | null;
    verified_at: string | null;
    verification_checked_at: string | null;
}

export function useAiCredential(workspaceId: string) {
    const [status, setStatus] = useState<AiCredentialStatus>(NOT_CONFIGURED);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    // A reply for a company the person has since switched away from is dropped.
    const current = useRef(workspaceId);
    current.current = workspaceId;

    const load = useCallback(async () => {
        const asked = workspaceId;
        setLoading(true);
        setError(null);
        const { data, error: failed } = await supabase().rpc('secret_status', {
            p_workspace: asked, p_kind: 'ai_api_key',
        });
        if (current.current !== asked) return;
        if (failed) {
            setError('We could not read the AI key status just now.');
            setLoading(false);
            return;
        }
        const row = (Array.isArray(data) ? data[0] : data) as StatusRow | undefined;
        setStatus(row?.configured ? {
            configured: true,
            provider: row.provider,
            configuredAt: row.configured_at,
            updatedAt: row.updated_at,
            verificationStatus: row.verification_status,
            verificationCategory: row.verification_category,
            verifiedAt: row.verified_at,
            checkedAt: row.verification_checked_at,
        } : NOT_CONFIGURED);
        setLoading(false);
    }, [workspaceId]);

    useEffect(() => { void load(); }, [load]);

    return { status, loading, error, reload: load };
}

export const saveAiKey = (workspaceId: string, provider: string, apiKey: string) =>
    apiPost<{ action: 'configured' | 'replaced'; verification: Verification }>(
        '/api/ai/credential/save', { workspaceId, provider, apiKey },
    );

export const verifyAiKey = (workspaceId: string) =>
    apiPost<{ verification: Verification }>('/api/ai/credential/verify', { workspaceId });

export const removeAiKey = (workspaceId: string) =>
    apiPost<{ removed: boolean }>('/api/ai/credential/remove', { workspaceId });
